import React, { useState, useEffect, Suspense, lazy } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useMediaQuery from "./hooks/useMediaQuery";
import { ThemeProvider } from "./contexts/ThemeContext";
import { initPostHog, trackEvent } from "./analytics";
import Sidebar from "./components/Sidebar";
import Hero from "./components/Hero";
import Footer from "./components/Footer";
import DarkModeToggle from "./components/DarkModeToggle";

const ProductTimeline = lazy(() => import("./components/ProductTimeline"));
const ProductPrinciples = lazy(() => import("./components/ProductPrinciples"));
const TechStack = lazy(() => import("./components/TechStack"));
const PRD = lazy(() => import("./components/PRD"));
const Contact = lazy(() => import("./components/Contact"));

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.32, 0.72, 0, 1],
      when: "beforeChildren",
    },
  },
};

function Loader() {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
    >
      <motion.div
        className="h-12 w-12 rounded-full bg-primary-500 will-change-transform"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.6, 1, 0.6],
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        aria-hidden
      />
    </motion.div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    // Initialize PostHog
    initPostHog();
    
    // Track dashboard opened
    const trackTimer = setTimeout(() => {
      trackEvent('dashboard_opened');
    }, 500);
    
    const timer = setTimeout(() => setLoading(false), 800);
    return () => {
      clearTimeout(timer);
      clearTimeout(trackTimer);
    };
  }, []);

  useEffect(() => {
    if (isMobile && menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, menuOpen]);

  // Track section views
  useEffect(() => {
    const sections = ['overview', 'product-timeline', 'product-principles', 'tech-stack', 'prd', 'contact'];
    const viewedSections = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const sectionId = entry.target.id;
            if (!viewedSections.has(sectionId)) {
              viewedSections.add(sectionId);
              trackEvent('section_viewed', {
                section: sectionId,
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
          <AnimatePresence mode="wait">
            {loading ? (
              <Loader key="loader" />
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="min-h-screen"
              >
              {/* Mobile controls - hamburger and dark mode toggle */}
              {isMobile && (
                <>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(true)}
                    className="fixed left-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-primary-600 md:hidden dark:bg-primary-600 dark:hover:bg-primary-700"
                    aria-label="Open menu"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </button>
                  {/* Floating dark mode toggle for mobile */}
                  <div className="fixed right-4 top-4 z-30 md:hidden">
                    <DarkModeToggle />
                  </div>
                </>
              )}

            <Sidebar
              isOpen={isMobile ? menuOpen : true}
              onClose={isMobile ? () => setMenuOpen(false) : undefined}
            />

            <main
              className={`min-h-screen bg-white dark:bg-gray-900 transition-[margin,background-color] duration-200
          pt-14 md:pt-0
              md:ml-[240px] lg:ml-[280px]`}
            >
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Hero />
              </motion.div>

              <div className="border-b border-gray-100 dark:border-gray-800" aria-hidden />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-[200px] items-center justify-center py-16">
                      <div className="h-6 w-6 animate-pulse rounded-full bg-primary-500/20 dark:bg-primary-400/30" />
                    </div>
                  }
                >
                  <ProductTimeline />
                </Suspense>
              </motion.div>

              <div className="border-b border-gray-100 dark:border-gray-800" aria-hidden />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-[200px] items-center justify-center py-16">
                      <div className="h-6 w-6 animate-pulse rounded-full bg-primary-500/20 dark:bg-primary-400/30" />
                    </div>
                  }
                >
                  <ProductPrinciples />
                </Suspense>
              </motion.div>

              <div className="border-b border-gray-100 dark:border-gray-800" aria-hidden />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-[200px] items-center justify-center py-16">
                      <div className="h-6 w-6 animate-pulse rounded-full bg-primary-500/20 dark:bg-primary-400/30" />
                    </div>
                  }
                >
                  <TechStack />
                </Suspense>
              </motion.div>

              <div className="border-b border-gray-100 dark:border-gray-800" aria-hidden />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-[200px] items-center justify-center py-16">
                      <div className="h-6 w-6 animate-pulse rounded-full bg-primary-500/20 dark:bg-primary-400/30" />
                    </div>
                  }
                >
                  <PRD />
                </Suspense>
              </motion.div>

              <div className="border-b border-gray-100 dark:border-gray-800" aria-hidden />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-[200px] items-center justify-center py-16">
                      <div className="h-6 w-6 animate-pulse rounded-full bg-primary-500/20 dark:bg-primary-400/30" />
                    </div>
                  }
                >
                  <Contact />
                </Suspense>
              </motion.div>

              <Footer />
            </main>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </ThemeProvider>
  );
}

export default App;
